import type { AccountHolderOwner, AccountHoldersAudienceFilter } from '../../../types/features/accountHolders';

export type AccountHoldersSortKey =
  | 'registration_date'
  | 'name'
  | 'state'
  | 'email_address'
  | 'phone_number'
  | 'phone_verified'
  | 'no_of_stores';

export type AccountHoldersSortDirection = 'asc' | 'desc';

export const accountHolderAudienceTabs: Array<{ value: AccountHoldersAudienceFilter; label: string }> = [
  { value: 'all_customers', label: 'All Customers' },
  { value: 'active_customers', label: 'Active Customers' },
  { value: 'trial_customers', label: 'Trial Customers' },
  { value: 'leads_with_stores', label: 'Leads with Stores' },
  { value: 'leads_without_stores', label: 'Leads without Stores' },
];

export const formatStoresCount = (count: number): string => String(count || 0).padStart(2, '0');

export const getNameInitials = (name: string): string => {
  const parts = (name || '').trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const second = parts[1]?.[0] || '';
  return `${first}${second}`.toUpperCase() || 'AH';
};

export const formatPhoneNumber = (value: string, includeCountryCode = false): string => {
  const digits = (value || '').replace(/\D/g, '');
  if (digits.length !== 10) return value || '-';

  const formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  return includeCountryCode ? `+1 ${formatted}` : formatted;
};

export const formatDate = (isoDate?: string): string => {
  if (!isoDate) return '-';
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return '-';

  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const year = parsed.getFullYear();
  return `${month}/${day}/${year}`;
};

export const formatDateTime = (isoDate?: string): string => {
  if (!isoDate) return '-';
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return '-';

  const date = formatDate(isoDate);
  let hours = parsed.getHours();
  const minutes = String(parsed.getMinutes()).padStart(2, '0');
  const meridian = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const hoursText = String(hours).padStart(2, '0');

  return `${date} ${hoursText}:${minutes}${meridian}`;
};

export const formatOwnerState = (owner: AccountHolderOwner): string => {
  const stateCode = owner.state_code || '';
  const stateName = owner.state_name || '';

  if (stateName && stateCode) {
    return `${stateName} (${stateCode})`;
  }

  return stateCode || '-';
};

export const formatOwnerStateCode = (owner: AccountHolderOwner): string => owner.state_code || '-';

export const getSubscriptionStatusClass = (status: string): 'active' | 'inactive' => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'active') return 'active';
  return 'inactive';
};

export const formatSubscriptionStatusLabel = (status: string): string => {
  if (!status) return '-';
  return `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
};

const getDateSortValue = (value?: string): number => {
  if (!value) return Number.MAX_SAFE_INTEGER;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
};

const normalizedStatuses = (owner: AccountHolderOwner): Set<string> =>
  new Set((owner.subscription_statuses || []).map((status) => status.toLowerCase()));

export const ownerHasTrialSubscription = (owner: AccountHolderOwner): boolean =>
  Boolean(owner.has_trial_subscription) || normalizedStatuses(owner).has('trial');

export const ownerHasPayingSubscription = (owner: AccountHolderOwner): boolean => {
  const statuses = normalizedStatuses(owner);
  return Boolean(owner.has_paying_subscription) || statuses.has('active') || statuses.has('pending_cancel');
};

export const ownerHasExpiredSubscription = (owner: AccountHolderOwner): boolean =>
  Boolean(owner.has_expired_subscription) || normalizedStatuses(owner).has('canceled');

export const ownerHasCustomerSubscription = (owner: AccountHolderOwner): boolean =>
  Boolean(owner.has_customer_subscription)
  || ownerHasTrialSubscription(owner)
  || ownerHasPayingSubscription(owner)
  || ownerHasExpiredSubscription(owner);

export const matchesAccountHolderAudience = (
  owner: AccountHolderOwner,
  audience: AccountHoldersAudienceFilter,
): boolean => {
  switch (audience) {
    case 'active_customers':
      return ownerHasPayingSubscription(owner) || ownerHasTrialSubscription(owner);
    case 'trial_customers':
      return ownerHasTrialSubscription(owner);
    case 'leads_with_stores':
      return !ownerHasCustomerSubscription(owner) && owner.no_of_stores > 0;
    case 'leads_without_stores':
      return owner.no_of_stores === 0;
    case 'all_customers':
    default:
      return true;
  }
};

export const matchesAccountHolderSearch = (owner: AccountHolderOwner, search: string): boolean => {
  const normalizedSearch = search.trim().toLowerCase();
  if (!normalizedSearch) return true;

  const normalizedPhoneSearch = normalizedSearch.replace(/\D/g, '');
  const rawPhone = owner.phone_number || '';
  const phoneDigits = rawPhone.replace(/\D/g, '');

  return [
    owner.name,
    owner.email_address,
    owner.state_code,
    owner.state_name,
    rawPhone,
    phoneDigits,
  ].some((value) => {
    const candidate = String(value || '').toLowerCase();
    return candidate.includes(normalizedSearch)
      || (normalizedPhoneSearch.length > 0 && candidate.includes(normalizedPhoneSearch));
  });
};

const getOwnerSortValue = (owner: AccountHolderOwner, key: AccountHoldersSortKey): string | number => {
  switch (key) {
    case 'phone_verified':
      return owner.phone_verified ? 1 : 0;
    case 'registration_date':
      return getDateSortValue(owner.registration_date);
    case 'state':
      return formatOwnerStateCode(owner).toLowerCase();
    case 'no_of_stores':
      return owner.no_of_stores || 0;
    default:
      return String(owner[key] || '').toLowerCase();
  }
};

export const sortAccountHolderOwners = (
  owners: AccountHolderOwner[],
  key: AccountHoldersSortKey,
  direction: AccountHoldersSortDirection,
): AccountHolderOwner[] => {
  const multiplier = direction === 'asc' ? 1 : -1;

  return [...owners].sort((leftOwner, rightOwner) => {
    const leftValue = getOwnerSortValue(leftOwner, key);
    const rightValue = getOwnerSortValue(rightOwner, key);

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      if (leftValue === rightValue) {
        return String(leftOwner.name || '').localeCompare(String(rightOwner.name || '')) * multiplier;
      }
      return (leftValue - rightValue) * multiplier;
    }

    const valueCompare = String(leftValue).localeCompare(String(rightValue), undefined, {
      numeric: true,
      sensitivity: 'base',
    });

    if (valueCompare !== 0) {
      return valueCompare * multiplier;
    }

    return String(leftOwner.name || '').localeCompare(String(rightOwner.name || '')) * multiplier;
  });
};

export const filterAndSortAccountHolderOwners = (
  owners: AccountHolderOwner[],
  search: string,
  audience: AccountHoldersAudienceFilter,
  key: AccountHoldersSortKey,
  direction: AccountHoldersSortDirection,
): AccountHolderOwner[] =>
  sortAccountHolderOwners(
    owners.filter((owner) => (
      matchesAccountHolderAudience(owner, audience)
      && matchesAccountHolderSearch(owner, search)
    )),
    key,
    direction,
  );
